// Shared store
import { DashboardState, initialDashboardState } from '@shared/store/dashboard.duck';
import { AuthState, initialAuthState } from '@shared/store/auth.duck';
import { AuthMachineState } from '@shared/auth/types';
import { initialNotificationState, NotificationState } from '@shared/store/notification.duck';
import { GeneralState, initialGeneralState } from '@shared/store/general.duck';
import { ConfigState, initialConfigState } from '@shared/store/config.duck';
import { DialogState, initialDialogState } from '@shared/store/dialog.duck';
import { CompanyStructureState, initialCompanyStructure } from '@shared/store/companyStructure.duck';
import { initialUserTags, UserTags } from '@shared/store/userTags.duck';
import { initialUserAvatarInfoHover, UserAvatarInfoHoverState } from '@shared/store/userAvatarInfoHover.duck';

// Remaining shared ducks
import { AddMemberDialogState, initialAddMemberState } from '@shared/store/addMemberDialog.duck';
import { AddTeamDialogState, initialAddTeamDialogState } from '@shared/store/addTeamDialog.duck';
import { initialWhereaboutsOptionsState, WhereaboutsOptionsState } from '@features/whereabouts/store/whereaboutsOptions.duck';

// Feature: Auth
import { ForgotPasswordState, initialForgotPasswordState } from '@features/auth/store/forgotPassword.duck';
import { initialRegistrationState, RegistrationState } from '@features/auth/store/registration.duck';
import { initialOnboardingState, OnboardingState } from '@features/auth/store/onboarding.duck';
import { initialLoginPageState, LoginPageState } from '@features/auth/pages/login/login.duck';

// Feature: Whereabouts
import { CompanyMovementsState, initialCompanyMovementsState } from '@features/whereabouts/store/companyMovements.duck';
import { EditMovementsState, initialEditMovementsState } from '@features/whereabouts/store/editMovements.duck';
import { initialOfficeViewState, OfficeViewState } from '@features/whereabouts/store/officeView.duck';
import { initialOfficeUsageState, OfficeUsageState } from '@features/whereabouts/store/officeUsage.duck';
import { FollowingTeamsAndTagsState, initialFollowingTeamsAndTagsState } from '@features/whereabouts/store/followingTeamsAndTags.duck';
import { DayNotesState, initialDayNotesState } from '@features/whereabouts/store/dayNotes.duck';
import { initialLineReportsWhereaboutsState, LineReportsWhereaboutsState } from '@features/whereabouts/store/LineReportsWhereabouts.duck';
import { FavouritedUsersWhereaboutsState, initialFavouritedUsersWhereaboutsState } from '@features/whereabouts/store/FavouritedUsersWhereabouts.duck';
import { initialManageDesksState, ManageDesksState } from '@features/whereabouts/pages/company-movements/ducks/manageDesks.duck';
import { CarParkingMapViewState, initialCarParkingMapViewState } from '@features/whereabouts/pages/company-movements/ducks/carParkingMapView.duck';
import { initialTeamMovementsState, TeamMovementsState } from '@features/whereabouts/pages/team-movements/models/team-movements.duck';
import { DepartmentsState, initialDepartmentsState } from '@features/whereabouts/pages/departments/data/departments.duck';
import { LockersState, initialLockersState } from '@features/whereabouts/pages/lockers/data/lockers.duck';
import { initialWhereaboutsViewState, WhereaboutsViewState } from '@features/whereabouts/store/whereaboutsView.duck';
import { DailyWhereaboutsState, initialDailyWhereaboutsState } from '@features/whereabouts/store/dailyWhereabouts.duck';
import { initialVisitorBookingState, VisitorBookingState } from '@features/whereabouts/pages/company-movements/components/visitor-booking/data/visitorBooking.duck';
import { initialKioskState, KioskState } from '@features/kiosk/data/kiosk.duck';
import { initialSignageState, SignageState } from '@features/signage/data/signage.duck';

// Feature: Holidays
import { HolidaysV2State, initialHolidaysV2State } from '@features/holidays/store/holidays-v2.duck';
import { HolidayBookingState, initialHolidayBooking } from '@features/holidays/store/holidayBooking.duck';
import { HolidaysState, initialHolidays } from '@features/holidays/store/holidays.duck';
import { ApprovalRequestsState, initialApprovalRequestsState } from '@features/holidays/store/approvalRequests.duck';
import { HolidayReportsState, initialHolidayReportsState } from '@features/holidays/pages/models/holiday-reports.duck';
import { HolidayAllowancesState, initialHolidayAllowancesState } from '@features/holidays/pages/models/holiday-allowances.duck';
import { HolidayCarryOverState, initialHolidayCarryOverState } from '@features/holidays/pages/carry-over/holiday-carry-over.duck';

// Feature: Desks
import { AdvanceHotDeskingSetupState, initialAdvanceHotDeskingSetupState } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { DeskBookingState, initialDeskBookingState } from '@features/desks/store/deskBooking.duck';
import { DeskReservationState, initialDeskReservation } from '@features/desks/store/deskReservation.duck';
import { EditDeskState, initialEditDesk } from '@features/desks/store/editDesk.duck';
import { EditOfficeLayout, initialEditOfficeLayout } from '@features/desks/store/editOfficeLayout.duck';
import { initialWeeklyDeskAvailability, WeeklyDeskAvailability } from '@features/desks/store/weeklyDeskAvailability.duck';

// Feature: Meeting Rooms
import { initialRoomFinderState, RoomFinderState } from '@features/meeting-rooms/pages/models/room-finder.duck';
import { initialMeetingRoomState, MeetingRoomState } from '@features/meeting-rooms/pages/models/meeting-room.duck';
import { initialMeetingRoomBookingDialogState, MeetingRoomBookingDialogState } from '@features/meeting-rooms/pages/models/meeting-room-booking-dialog.duck';

// Feature: Wall Planner
import { initialWallPlannerState, WallPlannerState } from '@features/wall-planner/pages/wallPlanner.duck';
import { initialWallPlannerHoverBoxState, WallPlannerHoverBoxState } from '@features/wall-planner/pages/data/WallPlannerHoverBox.duck';

// Feature: Reporting
import { initialKeyResponsibilitiesReportingState, KeyResponsibilitiesReportingState } from '@features/reporting/pages/key-responsibilities-report/key-responsibilities.duck';

// Feature: Settings
import { initialManagementState, ManagementState } from '@features/settings/store/management.duck';
import { initialUserManagementState, UserManagementState } from '@features/settings/store/userManagement.duck';
import { EditUserState, initialEditUserState } from '@features/settings/store/editUser.duck';
import { initialSetupCompanyState, SetupCompanyState } from '@features/settings/store/setupCompany.duck';
import { initialPaymentsState, PaymentsState } from '@features/settings/store/payments.duck';
import { initialOutlookSync, OutlookSync } from '@features/settings/store/outlookSync.duck';
import { EditCompanyState, initialEditCompanyState } from '@features/settings/pages/edit-company-page/editCompany.duck';
import { initialManageUsersState, ManageUsersState } from '@features/settings/pages/manage-users-page-v2/ducks/manage-users.duck';
import { AddUsersState, initialAddUsersState } from '@features/settings/pages/manage-users-page-v2/ducks/add-users.duck';
import { initialMeetingRoomSettingsState, MeetingRoomSettingsState } from '@features/settings/pages/meeting-rooms/meeting-rooms-settings.duck';
import { HospitalityManagementState, initialHospitalityManagementState } from '@features/settings/pages/office-management-page-v2/data/hospitality-management.duck';
import { EditCompanyLayout, initialEditCompanyLayout } from '@features/settings/pages/company-management-page-v2/data/editCompanyLayout.duck';

export interface AppState {
  auth: AuthState;
  authMachine: AuthMachineState;
  dashboard: DashboardState;
  editMovements: EditMovementsState;
  addMemberDialog: AddMemberDialogState;
  forgotPassword: ForgotPasswordState;
  addTeamDialog: AddTeamDialogState;
  registration: RegistrationState;
  onboarding: OnboardingState;
  management: ManagementState;
  notification: NotificationState;
  general: GeneralState;
  companyMovements: CompanyMovementsState;
  setupCompany: SetupCompanyState;
  loginPage: LoginPageState;
  config: ConfigState;
  editCompany: EditCompanyState;
  holidayBooking: HolidayBookingState;
  holidays: HolidaysState;
  companyStructure: CompanyStructureState;
  outlookSync: OutlookSync;
  payments: PaymentsState;
  wallPlanner: WallPlannerState;
  dialog: DialogState;
  advanceHotDeskingSetup: AdvanceHotDeskingSetupState;
  deskReservation: DeskReservationState;
  officeUsage: OfficeUsageState;
  editOfficeLayout: EditOfficeLayout;
  weeklyDeskAvailability: WeeklyDeskAvailability;
  officeView: OfficeViewState;
  deskBooking: DeskBookingState;
  editUser: EditUserState;
  approvalRequests: ApprovalRequestsState;
  userTags: UserTags;
  userManagement: UserManagementState;
  whereaboutsOptions: WhereaboutsOptionsState;
  holidaysV2: HolidaysV2State;
  editDesk: EditDeskState;
  followingTeamsAndTags: FollowingTeamsAndTagsState;
  keyResponsibilitiesReporting: KeyResponsibilitiesReportingState;
  roomFinder: RoomFinderState;
  meetingRoom: MeetingRoomState;
  meetingRoomSettings: MeetingRoomSettingsState;
  manageUsers: ManageUsersState;
  addUsers: AddUsersState;
  manageDesks: ManageDesksState;
  teamMovements: TeamMovementsState;
  wallPlannerHoverBox: WallPlannerHoverBoxState;
  holidayReports: HolidayReportsState;
  carParkingMapView: CarParkingMapViewState;
  visitorBooking: VisitorBookingState;
  kiosk: KioskState;
  signage: SignageState;
  departments: DepartmentsState;
  lockers: LockersState;
  lineReportsWhereabouts: LineReportsWhereaboutsState;
  holidayAllowances: HolidayAllowancesState;
  dayNotes: DayNotesState;
  userAvatarInfoHover: UserAvatarInfoHoverState;
  favouritedUsersWhereabouts: FavouritedUsersWhereaboutsState;
  holidayCarryOver: HolidayCarryOverState;
  hospitalityManagement: HospitalityManagementState;
  meetingRoomBookingDialog: MeetingRoomBookingDialogState;
  whereaboutsView: WhereaboutsViewState;
  dailyWhereabouts: DailyWhereaboutsState;
  editCompanyLayout: EditCompanyLayout;
}

const initialAuthMachineState: AuthMachineState = {
  lifecycle: 'unauthenticated',
  calendar: 'unknown',
  authType: null,
};

export const initialAppState: AppState = {
  auth: initialAuthState,
  authMachine: initialAuthMachineState,
  dashboard: initialDashboardState,
  editMovements: initialEditMovementsState,
  addMemberDialog: initialAddMemberState,
  forgotPassword: initialForgotPasswordState,
  addTeamDialog: initialAddTeamDialogState,
  registration: initialRegistrationState,
  onboarding: initialOnboardingState,
  management: initialManagementState,
  notification: initialNotificationState,
  general: initialGeneralState,
  companyMovements: initialCompanyMovementsState,
  setupCompany: initialSetupCompanyState,
  loginPage: initialLoginPageState,
  config: initialConfigState,
  editCompany: initialEditCompanyState,
  holidayBooking: initialHolidayBooking,
  holidays: initialHolidays,
  companyStructure: initialCompanyStructure,
  outlookSync: initialOutlookSync,
  payments: initialPaymentsState,
  wallPlanner: initialWallPlannerState,
  dialog: initialDialogState,
  advanceHotDeskingSetup: initialAdvanceHotDeskingSetupState,
  deskReservation: initialDeskReservation,
  officeUsage: initialOfficeUsageState,
  editOfficeLayout: initialEditOfficeLayout,
  weeklyDeskAvailability: initialWeeklyDeskAvailability,
  officeView: initialOfficeViewState,
  deskBooking: initialDeskBookingState,
  editUser: initialEditUserState,
  approvalRequests: initialApprovalRequestsState,
  userTags: initialUserTags,
  userManagement: initialUserManagementState,
  whereaboutsOptions: initialWhereaboutsOptionsState,
  holidaysV2: initialHolidaysV2State,
  editDesk: initialEditDesk,
  followingTeamsAndTags: initialFollowingTeamsAndTagsState,
  keyResponsibilitiesReporting: initialKeyResponsibilitiesReportingState,
  roomFinder: initialRoomFinderState,
  meetingRoom: initialMeetingRoomState,
  meetingRoomSettings: initialMeetingRoomSettingsState,
  manageUsers: initialManageUsersState,
  addUsers: initialAddUsersState,
  manageDesks: initialManageDesksState,
  teamMovements: initialTeamMovementsState,
  wallPlannerHoverBox: initialWallPlannerHoverBoxState,
  holidayReports: initialHolidayReportsState,
  carParkingMapView: initialCarParkingMapViewState,
  visitorBooking: initialVisitorBookingState,
  kiosk: initialKioskState,
  signage: initialSignageState,
  departments: initialDepartmentsState,
  lockers: initialLockersState,
  lineReportsWhereabouts: initialLineReportsWhereaboutsState,
  holidayAllowances: initialHolidayAllowancesState,
  dayNotes: initialDayNotesState,
  userAvatarInfoHover: initialUserAvatarInfoHover,
  favouritedUsersWhereabouts: initialFavouritedUsersWhereaboutsState,
  holidayCarryOver: initialHolidayCarryOverState,
  hospitalityManagement: initialHospitalityManagementState,
  meetingRoomBookingDialog: initialMeetingRoomBookingDialogState,
  whereaboutsView: initialWhereaboutsViewState,
  dailyWhereabouts: initialDailyWhereaboutsState,
  editCompanyLayout: initialEditCompanyLayout,
}
